import { Link } from "react-router";
import { Loader2 } from "lucide-react";

import { BrandModal } from "@/components/common/BrandModal";
import { formatMoney, formatNumber } from "@/lib/format";
import { TDM_CATEGORY_MAP, formatQty } from "@/lib/tdm-constants";
import { useTdmDrilldown } from "@/hooks/queries/useTdmStats";
import type { TdmFilterState } from "@/hooks/useTdmFilters";
import type { TdmCategory, TdmDrill } from "@/types/tdm";

/**
 * Las 10 obras que mueven el número que se clicó en un gráfico. Cuando el corte
 * trae `qtyCategory` la cifra y el orden son la cantidad convertida de esa
 * categoría; si no, el monto. Es la misma regla con la que el gráfico pintó.
 */
export function TdmDrilldownModal({
  drill,
  filters,
  onClose,
}: {
  drill: TdmDrill | null;
  filters: TdmFilterState;
  onClose: () => void;
}) {
  const { data, isFetching } = useTdmDrilldown(filters, drill);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  const qtyCat = drill?.qtyCategory;
  const unidad = qtyCat ? TDM_CATEGORY_MAP[qtyCat].unit : "";
  const cifra = (r: { amount: number; qty: number }) =>
    qtyCat ? formatQty(r.qty, unidad) : formatMoney(r.amount);

  return (
    <BrandModal
      open={!!drill}
      onOpenChange={(o) => !o && onClose()}
      title={drill?.title ?? ""}
      description={
        <>
          {drill?.subtitle ? <>{drill.subtitle}. </> : null}
          Las 10 obras que más aportan
          {qtyCat
            ? ` en ${TDM_CATEGORY_MAP[qtyCat].label.toLowerCase()} (${unidad})`
            : " al potencial de venta"}
          {isFetching ? "…" : total > 0 ? <> · {formatNumber(total)} en total</> : null}
        </>
      }
    >
      {isFetching && rows.length === 0 ? (
        <div className="py-12 text-center">
          <Loader2 className="mx-auto size-5 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No hay obras en este corte.
        </p>
      ) : (
        // El scroll va en la lista, no en el modal: `BrandModal` lleva
        // `overflow-hidden` y sin acotar aquí diez obras empujan el título fuera
        // de la pantalla.
        <ol className="-mr-2 max-h-[55vh] divide-y overflow-y-auto pr-2">
          {rows.map((r, i) => (
            <li key={r.contracting_id} className="flex gap-3 py-2.5">
              <span className="w-5 shrink-0 pt-0.5 text-xs font-bold text-muted-foreground">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/contracting/${r.contracting_id}`}
                  className="text-sm font-semibold text-blue-600 hover:underline dark:text-blue-400"
                >
                  {r.nomenclature}
                </Link>
                <p
                  className="mt-0.5 line-clamp-2 text-xs text-muted-foreground"
                  title={r.description}
                >
                  {r.description}
                </p>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground/70">
                    {r.entity}
                  </span>
                  <span>
                    · {r.department} · {r.sector}
                  </span>
                  {r.categorias.map((c) => (
                    <span
                      key={c}
                      className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white"
                      style={{
                        backgroundColor:
                          TDM_CATEGORY_MAP[c as TdmCategory]?.color ?? "#94a3b8",
                      }}
                    >
                      {TDM_CATEGORY_MAP[c as TdmCategory]?.label ?? c}
                    </span>
                  ))}
                </p>
              </div>
              <span className="shrink-0 pt-0.5 text-right text-sm font-bold tabular-nums">
                {cifra(r)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </BrandModal>
  );
}
